import {EmptyRepo} from "../../../components/Repo/EmptyRepo"
import {useGitDir} from "../../../hooks/useGitDir"
import {GitTree} from "../../../interfaces/repos"
import {RepoCodeRoutes} from "../../../routes/RepoCodeRoutes"
import {useCallback, useEffect, useState, useContext, useRef} from "react"
import git, {ReadCommitResult, TREE} from "isomorphic-git"
import {BackendContext} from "../../../context/BackendContext"
import {useParams, useNavigate, useLocation} from "react-router-dom"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import {History, DeviceHub} from "@mui/icons-material"
import FS from "@isomorphic-git/lightning-fs"
import styles from "./index.module.scss"
import { CommitsStatus } from "../../../components/Repo/CommitsStatus"

interface CodeProps {
  cloneCnt: number
}

export const Code = (props: CodeProps) => {
  const {fs, metadata} = useContext(BackendContext)
  const {orbitId, repoId} = useParams()
  const gitDir = useGitDir(orbitId, repoId)
  const params = useParams()

  const navigate = useNavigate()
  const location = useLocation()

  const [isEmptyRepo, setIsEmptyRepo] = useState<boolean>()
  const [gitTree, setGitTree] = useState<GitTree>({})
  const [branch, setBranch] = useState<string>("")
  const [allBranches, setAllBranches] = useState<string[]>([])
  const [commits, setCommits] = useState<ReadCommitResult[]>([])

  const branchAnchorEl = useRef<HTMLDivElement | null>(null)
  const [branchMenuOpen, setBranchMenuOpen] = useState(false)

  const walkTree = useCallback(async (
    fs: FS,
    gitDir: string,
    branch: string
  ) => {
    const results = await git.walk({
      fs: fs,
      gitdir: gitDir,
      trees: [TREE({ref: branch})],
      map: async (filepath, [head, _]) => {
        return {
          filepath,
          head
        }
      }
    })
    const tree: GitTree = {}
    for (const r of results) {
      tree[r.filepath] = r.head
    }
    setGitTree(tree)
  }, [])

  const countCommits = useCallback(
    async (
      fs: FS,
      gitDir: string,
      branch: string
    ) => {
      const commits = await git.log({fs, gitdir: gitDir, ref: branch})
      setCommits(commits.sort())
    }, []
  )

  const getAllBranches = useCallback(async (
    fs: FS,
    gitDir: string
  ) => {
    const branches = await git.listBranches({
      fs: fs,
      gitdir: gitDir
    })
    setAllBranches(branches)
    setIsEmptyRepo(branches.length === 0)
  }, [])

  const onSelectBranch = useCallback((event: any) => {
    const newBranch = event.target.innerText
    const currentPath = location.pathname
    if (currentPath === undefined) {
      return
    }
    const newPath = currentPath.replace(
      new RegExp(`/(blob|tree)/${branch}`),
      `/$1/${newBranch}`
    )
    navigate(newPath)
    setBranchMenuOpen(false)
  }, [branch, location, navigate])

  const onNavAllBranchesPage = (_: any) => {
    navigate('../branches')
    setBranchMenuOpen(false)
  }

  const onClickBranchSelector = useCallback((_: any) => {
    setBranchMenuOpen(true)
  }, [])

  const onCloseBranchSelector = useCallback((_: any) => {
    setBranchMenuOpen(false)
  }, [])

  // Update branch
  useEffect(() => {
    const path = params["*"]
    if (!path) {
      navigate("./tree/master")
      return
    }
    const parts = path.split("/")
    setBranch(parts[1])
  }, [params, navigate])

  // Parse tree when branch is determined
  useEffect(() => {
    if (!fs || !branch || !gitDir) {
      return
    }
    walkTree(fs, gitDir, branch).then()
    countCommits(fs, gitDir, branch).then()
  }, [walkTree, fs, branch, gitDir, props])

  // List all branches
  useEffect(() => {
    if (!fs || !gitDir) {
      return
    }
    getAllBranches(fs, gitDir).then()
  }, [getAllBranches, fs, gitDir])

  // If the Index info is not loaded yet, don't show anything
  if (isEmptyRepo === undefined || gitDir === undefined) {
    return null
  }

  // If it's a new repo, show the intro message
  if (isEmptyRepo) {
    return (
      <div className={styles.CodeContainer}>
        <EmptyRepo gitDir={gitDir} renameHost={metadata?.isGitSupported === true}/>
      </div>
    )
  }

  return (
    <div className={styles.CodeContainer}>
      <div>
        <div className={styles.HeaderRow}>
          <div className={styles.BranchSelector}
               onClick={onClickBranchSelector}
               ref={branchAnchorEl}
          >
            <div className={styles.CurrentBranch}>
              {branch}
            </div>
            <KeyboardArrowDownIcon/>
          </div>

          <div className={styles.BranchCountText}>
            <DeviceHub/>
            <div>{allBranches.length} {allBranches.length > 1 ? "branches" : "branch"}</div>
          </div>

          <div className="grow"/> {/**So that CommitStatus is always at the end */}
          <CommitsStatus commits={commits} href={`../commits/${branch}`}/>
        </div>

        <RepoCodeRoutes gitTree={gitTree} cloneCnt={props.cloneCnt}/>

        <Menu
          id="branch-menu"
          open={branchMenuOpen}
          anchorEl={branchAnchorEl.current}
          onClose={onCloseBranchSelector}
          classes={{paper: styles.BranchMenuPaper, list: styles.BranchMenuList}}
        >
          {
            allBranches.map((b) => (
              <MenuItem
                key={b}
                onClick={onSelectBranch}
                classes={{
                  root: styles.BranchMenu, 
                  gutters: styles.BranchMenuItem
                }}
              >
                {b}
              </MenuItem>
            ))
          }
          <MenuItem 
            onClick={onNavAllBranchesPage} 
            classes={{
              root: styles.BranchMenu,
              gutters: styles.LastBranchMenuItem
            }
          }>
            View all branches
          </MenuItem>
        </Menu>

      </div>
    </div>
  )
}
